/**
 * Open/closed state of turn folds ("Worked for 4m 12s" rows), per thread.
 *
 * In memory only — a reload starts every fold closed again (stopped turns
 * start open; see `MessageFold.autoExpand`).  Kept at module level, keyed by
 * thread id, so it survives the chat area remounting (thread switch, revert)
 * within the session.
 *
 * A message with no entry shows its default (`autoExpand`).  A fold that
 * renders open by default records that as an explicit entry, so that when the
 * next run starts `collapseAllFolds` can close it along with the ones the user
 * opened by hand.
 */
import { useCallback, useSyncExternalStore } from 'react';

const byThread = new Map<string, Map<string, boolean>>();
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function threadMap(threadId: string): Map<string, boolean> {
  let m = byThread.get(threadId);
  if (!m) {
    m = new Map();
    byThread.set(threadId, m);
  }
  return m;
}

export function setFoldOpen(threadId: string, messageId: string, open: boolean): void {
  const m = threadMap(threadId);
  if (m.get(messageId) === open) return;
  m.set(messageId, open);
  emit();
}

/** Close every fold in the thread — called when a new run starts there. */
export function collapseAllFolds(threadId: string): void {
  const m = byThread.get(threadId);
  if (!m) return;
  let changed = false;
  for (const [id, open] of m) {
    if (open) {
      m.set(id, false);
      changed = true;
    }
  }
  if (changed) emit();
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}

/**
 * `[open, setOpen]` for one message's fold.  `undefined` from the store means
 * "never toggled": the caller falls back to the fold's default.
 */
export function useFoldOpen(threadId: string, messageId: string): [boolean | undefined, (open: boolean) => void] {
  const open = useSyncExternalStore(
    subscribe,
    () => byThread.get(threadId)?.get(messageId),
  );
  const set = useCallback((next: boolean) => setFoldOpen(threadId, messageId, next), [threadId, messageId]);
  return [open, set];
}
